/* ═══════════════════════════════════════════════════════════════════════════
   Les sons d'INTERFACE, par DÉLÉGATION (PLAN.md §8, J14).

   Plutôt que de câbler un son dans chaque bouton — dispersé, oubliable, dupliqué
   à chaque écran — on écoute la racine de l'arbre React UNE fois. Les événements
   remontent (`click`, `focusin`, `change` bouillonnent), et un seul jeu de règles
   décide du son. Ajouter un écran ne demande alors AUCUN branchement : ses
   boutons sonnent déjà.

   Deux finesses d'UX :

   1. La NAVIGATION (`ui_move`) ne se joue qu'au clavier/manette, jamais à la
      souris. Sinon un clic — qui déplace le focus PUIS déclenche l'action —
      produirait deux sons (le move du focus, puis la sélection). On distingue
      l'intention avec un drapeau clavier/pointeur, exactement comme `:focus-visible`.

   2. Le son d'un clic se DÉDUIT de l'élément : un bouton primaire valide, un
      « RETOUR » revient, un nœud de hangar achète (ou refuse s'il est verrouillé).
      Un attribut `data-sfx` sur l'élément a toujours le dernier mot, pour les cas
      qu'aucune règle ne couvre.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Tous les identifiants de son que cette couche peut émettre. Exporté pour que
 *  le test de jointure vérifie qu'ils existent tous dans le synthé. */
export const UI_SFX_IDS = [
  'ui_move',
  'ui_select',
  'ui_confirm',
  'ui_back',
  'ui_toggle',
  'ui_denied',
  'ui_purchase',
] as const;

const INTERACTIVE = 'button, .node, input, select, [role="button"], [tabindex]';

/** Le son d'un clic, déduit de l'élément (un `data-sfx` explicite prime). */
function soundForClick(el: HTMLElement): string {
  const explicit = el.dataset['sfx'];
  if (explicit) return explicit;

  // Nœud de hangar : acheter, ou refuser si verrouillé, ou juste sélectionner
  // s'il est déjà acquis.
  if (el.classList.contains('node')) {
    if (el.classList.contains('owned')) return 'ui_select';
    if (el.classList.contains('locked')) return 'ui_denied';
    return 'ui_purchase';
  }
  if ((el.textContent ?? '').trim().toUpperCase() === 'RETOUR') return 'ui_back';
  if (el.classList.contains('btn-primary')) return 'ui_confirm';
  return 'ui_select';
}

/**
 * Attache les écouteurs sonores à la racine de l'UI. `play` est fourni par
 * l'appelant (il connaît l'AudioDirector et la catégorie) — cette couche ne
 * connaît que le DOM et les identifiants de son. Appelée UNE fois au démarrage.
 */
export function installUiSounds(root: HTMLElement, play: (id: string) => void): void {
  let keyboard = false;

  // Les touches de NAVIGATION marquent l'intention clavier ; un pointeur la lève.
  root.addEventListener('keydown', (e) => {
    if (e.key === 'Tab' || e.key.startsWith('Arrow')) keyboard = true;
  });
  root.addEventListener('pointerdown', () => {
    keyboard = false;
  });

  // Navigation : le focus arrive sur un élément interactif, au clavier seulement.
  root.addEventListener('focusin', (e) => {
    if (!keyboard) return;
    const el = e.target as HTMLElement;
    if (el.matches?.(INTERACTIVE)) play('ui_move');
  });

  // Activation.
  root.addEventListener('click', (e) => {
    const el = (e.target as HTMLElement).closest?.(
      'button, .node, [role="button"]',
    ) as HTMLElement | null;
    if (!el) return;
    play(soundForClick(el));
  });

  // Bascule d'un réglage. `change` (pas `input`) : une fois par ajustement
  // confirmé, jamais à chaque pixel d'un curseur qu'on fait glisser.
  root.addEventListener('change', (e) => {
    const el = e.target as HTMLElement;
    if (el.tagName === 'INPUT' || el.tagName === 'SELECT') play('ui_toggle');
  });
}
